import { useEffect, useState } from "react";
import { RefreshCw, WifiOff } from "lucide-react";
import api from "@/lib/api";

// Single, non-stacking connectivity strip driven by lib/api.js events. Never touches the auth session.
export function ConnectionBanner() {
  const [state, setState] = useState(null); // null | "reconnecting" | "offline"
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const reconnecting = (e) => { setState("reconnecting"); setDetail(e?.detail || ""); };
    const offline = (e) => { setState("offline"); setDetail(e?.detail || ""); };
    // Recovered after a full outage → pages that gave up loading hold empty data; one reload refreshes them (cookie session survives).
    const online = () => setState((s) => { if (s === "offline") window.location.reload(); setDetail(""); return null; });
    window.addEventListener("sf:reconnecting", reconnecting);
    window.addEventListener("sf:offline", offline);
    window.addEventListener("sf:online", online);
    return () => { window.removeEventListener("sf:reconnecting", reconnecting); window.removeEventListener("sf:offline", offline); window.removeEventListener("sf:online", online); };
  }, []);

  const retry = async () => {
    setBusy(true);
    try { await api.get("/health"); window.location.reload(); }
    catch { setState("offline"); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  const offline = state === "offline";
  return (
    <div role="status" aria-live="polite" data-testid={offline ? "connection-offline-banner" : "connection-reconnecting-banner"}
         className={`fixed top-0 inset-x-0 z-[65] flex items-center justify-center gap-3 px-4 py-1.5 text-xs font-mono ${offline ? "bg-red-950/95 text-red-200 border-b border-red-800" : "bg-slate-900/95 text-amber-300 border-b border-amber-500/30"}`}>
      {offline ? <WifiOff className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
      {offline ? <span>Connection lost — {detail || "the server did not respond"}. Your session is preserved.</span> : <span>Reconnecting…</span>}
      {offline && (
        <button onClick={retry} disabled={busy} className="inline-flex items-center gap-1 border border-red-700 hover:border-red-400 rounded px-2 py-0.5 text-red-100" data-testid="connection-retry-button">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Retry
        </button>
      )}
    </div>
  );
}
